import React, { useEffect, useState } from 'react';
import { useIntersectionObserver } from '../utils/animations';
import MusicToggle from './MusicToggle';

// Nueva imagen de fondo con detalles botánicos
const HERO_IMAGE = '/lovable-uploads/5ea40fea-4228-4fee-bb9d-755c237e1d14.png';

// Fecha de la boda: 12 de agosto de 2025
const WEDDING_DATE = new Date('2025-08-12T16:00:00');

// Color base para la transición
const BASE_COLOR = '#fdf8f4';

// Define the animation keyframes here instead of in JSX
const gentleBreezeAnimation = `
  @keyframes gentleBreeze {
    0% {
      transform: translate(0, 0) scale(1.02);
    }
    25% {
      transform: translate(-0.5%, 0.5%) scale(1.03);
    }
    50% {
      transform: translate(0.3%, -0.3%) scale(1.04);
    }
    75% {
      transform: translate(-0.3%, -0.2%) scale(1.03);
    }
    100% {
      transform: translate(0.5%, 0.4%) scale(1.02);
    }
  }
`;

const Hero: React.FC = () => {
  const [animateContent, setAnimateContent] = useState(false);
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  
  useEffect(() => {
    // Add the animation styles to the document head
    const styleElement = document.createElement('style');
    styleElement.innerHTML = gentleBreezeAnimation;
    document.head.appendChild(styleElement);
    
    // Cleanup function to remove the style when component unmounts
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);
  
  useEffect(() => {
    // Iniciar animación al cargar la página
    const timer = setTimeout(() => setAnimateContent(true), 300);
    
    // Countdown timer
    const calculateTimeLeft = () => {
      const difference = WEDDING_DATE.getTime() - new Date().getTime();
      
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    };
    
    // Initial calculation
    calculateTimeLeft();
    
    // Update every second
    const countdownTimer = setInterval(calculateTimeLeft, 1000);
    
    return () => {
      clearTimeout(timer);
      clearInterval(countdownTimer);
    };
  }, []);
  
  const { ref: scrollIndicatorRef, isVisible: scrollVisible } = useIntersectionObserver({
    threshold: 0.1,
    rootMargin: '0px'
  });

  // Función para formatear números con ceros a la izquierda
  const formatNumber = (num: number): string => {
    return num < 10 ? `0${num}` : `${num}`;
  };

  return (
    <section id="hero" className="relative h-screen w-full overflow-hidden bg-cream">
      {/* Animated background with botanical pattern - con nueva imagen */}
      <div 
        className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
        style={{ 
          backgroundImage: `url(${HERO_IMAGE})`,
          animation: 'gentleBreeze 25s ease-in-out infinite alternate'
        }}
      />
      
      {/* No necesitamos un overlay adicional, la imagen ya tiene un fondo claro */}
      
      {/* Degradado vertical en la parte inferior para transición suave */}
      <div 
        className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none"
        style={{ 
          background: `linear-gradient(to bottom, transparent, ${BASE_COLOR})`,
          opacity: 0.85
        }}
      ></div>
      
      {/* Hero content - Ajustado para que el título esté más abajo */}
      <div className="relative z-10 h-full w-full flex flex-col items-center justify-center px-4 text-center pt-16">
        <div className="max-w-4xl mx-auto">
          {/* Names animation - más suave y elegante */}
          <div 
            className={`mb-4 transition-all duration-\[1600ms\] ease-in-out transform will-change-transform ${
              animateContent ? 'opacity-100 translate-y-0 blur-0' : 'opacity-0 translate-y-5 blur-[4px]'
            }`}
          >
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-parisienne text-forest mb-2 tracking-wide">
              Emi & Carlos
            </h1>
          </div>
          
          {/* Date animation */}
          <div 
            className={`mb-6 transition-all duration-\[1600ms\] ease-in-out transform will-change-transform ${
              animateContent ? 'opacity-100 translate-y-0 blur-0 delay-\[200ms\]' : 'opacity-0 translate-y-5 blur-[4px]'
            }`}
          >
            <p className="text-xl md:text-2xl text-forest/90 font-baskerville tracking-widest">
              16 · AGOSTO · 2025
            </p>
          </div>
          
          {/* Mensaje de invitación - con delay escalonado */}
          <div 
            className={`mb-4 transition-all duration-\[1600ms\] ease-in-out transform will-change-transform ${
              animateContent ? 'opacity-100 translate-y-0 blur-0 delay-\[400ms\]' : 'opacity-0 translate-y-5 blur-[4px]'
            }`}
          >
           
            <p className="text-xl text-forest/90 font-baskerville">
              Te esperamos en...
            </p>
          </div>
          
          {/* Countdown - con mayor delay */}
          <div 
            className={`mb-6 transition-all duration-\[1600ms\] ease-in-out transform will-change-transform ${
              animateContent ? 'opacity-100 translate-y-0 blur-0 delay-\[600ms\]' : 'opacity-0 translate-y-5 blur-[4px]'
            }`}
          >
            <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 shadow-md">
              <div className="grid grid-cols-4 gap-4">
                <div className="flex flex-col items-center">
                  <span className="text-3xl md:text-4xl font-baskerville text-forest">{timeLeft.days}</span>
                  <span className="text-xs uppercase tracking-wider text-forest/80 mt-1">Días</span>
                </div>
                
                <div className="flex flex-col items-center">
                  
                  <span className="text-3xl md:text-4xl font-baskerville text-forest">{formatNumber(timeLeft.hours)}</span>
                  <span className="text-xs uppercase tracking-wider text-forest/80 mt-1">Horas</span>
                </div>
                
                <div className="flex flex-col items-center">
                  <span className="text-3xl md:text-4xl font-baskerville text-forest">{formatNumber(timeLeft.minutes)}</span>
                  <span className="text-xs uppercase tracking-wider text-forest/80 mt-1">Minutos</span>
                </div>
                
                <div className="flex flex-col items-center">
                  <span className="text-3xl md:text-4xl font-baskerville text-forest">{formatNumber(timeLeft.seconds)}</span>
                  <span className="text-xs uppercase tracking-wider text-forest/80 mt-1">Segundos</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Nueva sección de imagen 7.jpg debajo del contador - posición ajustada */}
          <div 
            className={`transition-all duration-\[1600ms\] ease-in-out transform will-change-transform ${
              animateContent ? 'opacity-100 translate-y-0 blur-0 delay-\[800ms\]' : 'opacity-0 translate-y-5 blur-[4px]'
            }`}
          >
            <div className="relative w-full max-w-xl mx-auto">
              <div className="absolute -top-10 -left-4 w-20 h-20 opacity-30 rotate-[-15deg]">
                <img 
                  src="/lovable-uploads/1.svg" 
                  alt="" 
                  className="w-full h-full object-contain"
                  aria-hidden="true"
                />
              </div>
              <div className="absolute -bottom-8 -right-4 w-20 h-20 opacity-30 rotate-[15deg]">
                <img 
                  src="/lovable-uploads/2.svg" 
                  alt="" 
                  className="w-full h-full object-contain"
                  aria-hidden="true"
                />
              </div>
              
              <div className="overflow-hidden rounded-lg shadow-lg border-2 border-sage/20">
                <img 
                  src="/lovable-uploads/7.jpg" 
                  alt="Emi y Carlos" 
                  className="w-full h-auto transform hover:scale-105 transition-transform duration-500"
                  style={{ 
                    height: '34vh', 
                    objectFit: 'cover', 
                    objectPosition: 'center 75%' 
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Music Toggle en la esquina inferior derecha */}
      <div className="absolute bottom-8 right-8 z-20">
        <MusicToggle fixed={false} showDelay={1000} inHero={true} />
      </div>
    </section>
  );
};

export default Hero;
